/**
 * GiftPass math. GiftPass is ₱299 fixed and stackable (PRD / vault).
 * Pure functions over plain data — safe to run on client and in Deno Edge Functions.
 */
export const GIFTPASS_VALUE_PHP = 299;

/**
 * Apply N stacked GiftPasses against a subtotal (in PHP).
 * Never returns a negative payable; reports how much pass value was actually used.
 */
export function applyGiftPasses(
  subtotalPhp: number,
  passCount: number,
): { payablePhp: number; passValueUsedPhp: number; passesConsumed: number } {
  if (subtotalPhp < 0) throw new RangeError("subtotal cannot be negative");
  if (passCount < 0 || !Number.isInteger(passCount))
    throw new RangeError("passCount must be a non-negative integer");

  const available = passCount * GIFTPASS_VALUE_PHP;
  const used = Math.min(available, subtotalPhp);
  const passesConsumed = Math.ceil(used / GIFTPASS_VALUE_PHP);

  return {
    payablePhp: subtotalPhp - used,
    passValueUsedPhp: used,
    passesConsumed,
  };
}

/**
 * Spread one booking's GiftPass value across the rows of a booking group.
 *
 * ═══ WHY COUNTS CANNOT BE DISTRIBUTED, ONLY VALUE ═══
 *
 * `computeBookingAmounts` takes a `giftpassCount` per ROW, but a GiftPass
 * belongs to the BOOKING, not to one person in it — PRD §6.2 stacks ₱299 cards
 * against the whole bill. Handing each row a whole number of passes cannot be
 * right whenever a row's gross is not a multiple of ₱299: a ₱698 row and a ₱299
 * row sharing three passes have no integer split that spends ₱897 correctly.
 *
 * So the split is done in PESOS. Each row absorbs as much of the remaining
 * value as its own gross can take, in order, and whatever is left over is
 * FORFEITED — GiftPass gives no change (Business Logic §5).
 *
 * ⚠ `commission_base` is NOT touched by any of this. Commission is always on
 * the full promo price, including sessions paid entirely by GiftPass
 * (PRD §6.5, money-math.md §1) — the therapist is paid the same whether the
 * client handed over cards or cash.
 */
export interface GiftpassDistribution {
  /** Per row, in the order given: the pass value that row absorbed. */
  appliedPhp: number[];
  /** Value that exceeded the whole group's gross — no change given. */
  forfeitedPhp: number;
}

export function distributeGiftpassValue(
  rowGrossPhp: readonly number[],
  passCount: number,
): GiftpassDistribution {
  if (passCount < 0 || !Number.isInteger(passCount)) {
    throw new RangeError("passCount must be a non-negative integer");
  }
  if (rowGrossPhp.some((gross) => gross < 0)) {
    throw new RangeError("row gross cannot be negative");
  }

  let remaining = passCount * GIFTPASS_VALUE_PHP;
  const appliedPhp = rowGrossPhp.map((gross) => {
    const used = Math.min(remaining, gross);
    remaining = round2(remaining - used);
    return round2(used);
  });

  return { appliedPhp, forfeitedPhp: round2(remaining) };
}

/** Money stays 2-decimal — never floats drifting (money-math.md §5). */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
